// Tabs with roving tabindex (WAI-ARIA tabs pattern): arrows/Home/End move, activation follows
// focus. Labels wrap rather than truncate (text expansion, §4.5).
import { useId, useRef, type KeyboardEvent, type ReactNode } from "react";
import { cx } from "../lib";

export interface TabItem {
  id: string;
  label: ReactNode;
  /** Count that needs action ("Needs action 14"), never a decorative dot. */
  count?: number;
  panel: ReactNode;
}

export interface TabsProps {
  items: TabItem[];
  value: string;
  onValueChange: (id: string) => void;
  label: string;
  className?: string;
  "data-testid"?: string;
}

export function Tabs({
  items,
  value,
  onValueChange,
  label,
  className,
  "data-testid": testId,
}: TabsProps) {
  const base = useId();
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const index = Math.max(
    0,
    items.findIndex((i) => i.id === value),
  );
  const move = (to: number) => {
    const n = (to + items.length) % items.length;
    const item = items[n];
    if (!item) return;
    onValueChange(item.id);
    refs.current[n]?.focus();
  };
  const onKey = (e: KeyboardEvent) => {
    const keys: Record<string, () => void> = {
      ArrowRight: () => move(index + 1),
      ArrowLeft: () => move(index - 1),
      Home: () => move(0),
      End: () => move(items.length - 1),
    };
    const fn = keys[e.key];
    if (fn) {
      e.preventDefault();
      fn();
    }
  };
  const active = items[index];
  return (
    <div className={className} data-testid={testId}>
      <div
        role="tablist"
        aria-label={label}
        onKeyDown={onKey}
        className="ady-scroll-quiet flex gap-1 overflow-x-auto overflow-y-hidden border-b border-subtle"
      >
        {items.map((item, i) => {
          const selected = i === index;
          return (
            <button
              key={item.id}
              ref={(el) => {
                refs.current[i] = el;
              }}
              type="button"
              role="tab"
              id={`${base}-tab-${item.id}`}
              aria-selected={selected}
              aria-controls={`${base}-panel-${item.id}`}
              tabIndex={selected ? 0 : -1}
              onClick={() => onValueChange(item.id)}
              className={cx(
                "relative flex min-h-11 flex-none items-center gap-2 px-3 text-md font-medium",
                selected ? "text-fg" : "text-fg-secondary hover:text-fg",
                "after:absolute after:inset-x-2 after:-bottom-px after:h-0.5 after:rounded-full",
                selected ? "ady-tab-active after:bg-brand-blue" : "after:bg-transparent",
              )}
            >
              <span className={selected ? "ady-text-brand" : undefined}>{item.label}</span>
              {item.count !== undefined ? (
                <span className="ady-ledger rounded-xs bg-neutral-bg px-1.5 text-2xs text-neutral-fg">
                  {item.count}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
      {active ? (
        <div
          role="tabpanel"
          id={`${base}-panel-${active.id}`}
          aria-labelledby={`${base}-tab-${active.id}`}
          tabIndex={0}
          className="pt-4"
        >
          {active.panel}
        </div>
      ) : null}
    </div>
  );
}
